import React from 'react'
import { Row, Col, Card, Statistic, List, Button, Tag, Typography, Space } from 'antd'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  HomeOutlined,
  BuildOutlined,
  EyeOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'

const { Title, Text } = Typography

export const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { customer, business } = useCustomerAuth()

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => customerApi.getDashboardData(),
  })

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'in_progress':
      case 'in-progress':
        return <ClockCircleOutlined style={{ color: '#fa8c16' }} />
      case 'scheduled':
        return <CalendarOutlined style={{ color: '#1890ff' }} />
      default:
        return <ExclamationCircleOutlined style={{ color: '#666' }} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'in_progress':
      case 'in-progress':
        return 'processing'
      case 'scheduled':
        return 'default'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not scheduled'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div>
      {/* Welcome Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px', fontSize: '24px' }}>
          Welcome back, {customer?.firstName}!
        </Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          Here's what's happening with your services at {business?.name}
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Jobs"
              value={dashboardData?.stats.totalJobs || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Jobs"
              value={dashboardData?.stats.activeJobs || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Completed Jobs"
              value={dashboardData?.stats.completedJobs || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Unpaid Invoices"
              value={dashboardData?.stats.unpaidInvoices || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Active Jobs */}
        <Col xs={24} lg={12}>
          <Card
            title="Active Jobs"
            extra={
              <Button 
                type="link" 
                onClick={() => navigate('/jobs')}
                icon={<RightOutlined />}
              >
                View All
              </Button>
            }
            loading={isLoading}
          >
            <List
              dataSource={dashboardData?.activeJobs || []}
              locale={{ emptyText: 'No active jobs' }}
              renderItem={(job) => (
                <List.Item
                  actions={[
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      View Details
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={getStatusIcon(job.status)}
                    title={
                      <Space>
                        <Text strong>{job.title}</Text>
                        <Tag color={getStatusColor(job.status)}>
                          {job.status.replace('_', ' ').toUpperCase()}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">{job.serviceAddress}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Scheduled: {formatDate(job.scheduledDate)}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Recent Invoices */}
        <Col xs={24} lg={12}>
          <Card
            title="Recent Invoices"
            extra={
              <Button 
                type="link" 
                onClick={() => navigate('/invoices')}
                icon={<RightOutlined />}
              >
                View All
              </Button>
            }
            loading={isLoading}
          >
            <List
              dataSource={dashboardData?.recentInvoices || []}
              locale={{ emptyText: 'No recent invoices' }}
              renderItem={(invoice) => (
                <List.Item
                  actions={[
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      View
                    </Button>,
                    ...(invoice.status === 'unpaid' ? [
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => navigate(`/invoices/${invoice.id}/pay`)}
                      >
                        Pay Now
                      </Button>
                    ] : [])
                  ]}
                >
                  <List.Item.Meta
                    avatar={<DollarOutlined style={{ color: '#1890ff' }} />}
                    title={
                      <Space>
                        <Text strong>#{invoice.invoiceNumber}</Text>
                        <Tag color={invoice.status === 'paid' ? 'success' : 'error'}>
                          {invoice.status.toUpperCase()}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Text strong>{formatCurrency(invoice.amount)}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {invoice.status === 'paid' 
                            ? `Paid: ${formatDate(invoice.paidDate)}`
                            : `Due: ${formatDate(invoice.dueDate)}`
                          }
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Project Visualizer Promotion */}
      <Row style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card 
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              color: 'white'
            }}
            styles={{ body: { padding: '24px 16px' } }}
          >
            <Row align="middle">
              <Col xs={24} md={16}>
                <Space direction="vertical" size="small">
                  <Title level={3} style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                    🏗️ Visualize Your Next Project
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                    Design ADUs, pools, and outdoor structures with our AI-powered 3D visualizer. 
                    Get instant quotes and see your vision come to life!
                  </Text>
                  <Space wrap>
                    <HomeOutlined style={{ color: 'white' }} />
                    <Text style={{ color: 'white', fontSize: '12px' }}>ADU Design</Text>
                    <BuildOutlined style={{ color: 'white' }} />
                    <Text style={{ color: 'white', fontSize: '12px' }}>Pool Planning</Text>
                    <EyeOutlined style={{ color: 'white' }} />
                    <Text style={{ color: 'white', fontSize: '12px' }}>3D Visualization</Text>
                  </Space>
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'center', marginTop: '16px' }}>
                <Button 
                  type="primary"
                  size="large"
                  onClick={() => navigate('/visualizer')}
                  block
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid white',
                    color: 'white',
                    height: '48px',
                    fontSize: '16px'
                  }}
                >
                  Start Designing
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Upcoming Appointments */}
      {dashboardData?.upcomingAppointments && dashboardData.upcomingAppointments.length > 0 && (
        <Row style={{ marginTop: '16px' }}>
          <Col span={24}>
            <Card title="Upcoming Appointments">
              <List
                dataSource={dashboardData.upcomingAppointments}
                renderItem={(appointment) => (
                  <List.Item
                    actions={[
                      <Button 
                        type="link" 
                        onClick={() => navigate(`/jobs/${appointment.id}`)}
                      >
                        View Details
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<CalendarOutlined style={{ color: '#1890ff' }} />}
                      title={<Text strong>{appointment.title}</Text>}
                      description={
                        <div>
                          <Text type="secondary">{appointment.serviceAddress}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatDate(appointment.scheduledDate)}
                          </Text>
                          {appointment.technician && (
                            <>
                              <br />
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Technician: {appointment.technician.firstName} {appointment.technician.lastName}
                              </Text>
                            </>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  )
}
